import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import IndividualRehabPlanTwoForm from "./IndividualRehabPlanTwoForm";
import {
  fetchIndividualRehabPlanTwo,
  updateIndividualRehabPlanTwo,
  clearCurrentIndividualRehabPlanTwo,
} from "../../store/slices/individualRehabPlanTwoSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";

const toDate = (v) => (v ? String(v).substring(0, 10) : "");

// Parent-level date columns that need yyyy-mm-dd normalisation for <input type="date">
const PARENT_DATE_FIELDS = [
  "date_of_plan", "date_of_birth",
  "review_twice_weekly_date", "review_weekly_date", "review_monthly_date",
  "review_quarterly_date", "review_6monthly_date",
  "stage1_date_achieved", "stage2_date_achieved", "stage3_date_achieved", "stage4_date_achieved",
  "outcome_review_date", "client_sign_date", "prp_sign_date", "family_sign_date",
  "other_sign_date", "next_scheduled_review",
];

const EditIndividualRehabPlanTwo = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { currentIndividualRehabPlanTwo, isLoading, error } = useSelector(
    (s) => s.individualRehabPlanTwo
  );

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",
    recovery_capitals: [],
    strengths_growths: [],
    objectives: [],
    self_reviews: [],
  });

  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    if (id) dispatch(fetchIndividualRehabPlanTwo(id));
    return () => dispatch(clearCurrentIndividualRehabPlanTwo());
  }, [dispatch, id]);

  useEffect(() => {
    const rec = currentIndividualRehabPlanTwo;
    if (rec && rec.id === parseInt(id)) {
      const {
        // API serializes relations as snake_case keys — these match formData keys
        recovery_capitals,
        strengths_growths,
        objectives,
        self_reviews,
        smart_goals,
        patient, case: caseRel, creator, updater, deleter,
        created_at, updated_at, deleted_at,
        ...scalars
      } = rec;

      const normalized = { ...scalars };
      PARENT_DATE_FIELDS.forEach((f) => { normalized[f] = toDate(scalars[f]); });

      setFormData({
        ...normalized,
        recovery_capitals: recovery_capitals || [],
        strengths_growths: strengths_growths || [],
        objectives: (objectives || []).map((o) => ({ ...o, target_date: toDate(o.target_date) })),
        self_reviews: (self_reviews || []).map((r) => ({ ...r, review_date: toDate(r.review_date) })),
        smart_goals: (smart_goals || []).map((g) => ({ ...g, start_date: toDate(g.start_date), end_date: toDate(g.end_date) })),
      });

      if (rec.patient_id) {
        dispatch(fetchPatient(rec.patient_id))
          .unwrap()
          .then((patient) => setPatientData(patient))
          .catch((err) => console.error("Failed to fetch patient details:", err));
      }
    }
  }, [currentIndividualRehabPlanTwo, id, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateIndividualRehabPlanTwo({ id, formData })).unwrap();
      Swal.fire({
        title: "Success!",
        text: "Individual Rehabilitation Plan (2) updated successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      let errorMessage = "Failed to update Individual Rehabilitation Plan (2)";
      if (typeof err === "string") errorMessage = err;
      else if (typeof err === "object" && err !== null) errorMessage = Object.values(err).flat().join("\n");
      Swal.fire({ title: "Error", text: errorMessage, icon: "error" });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />

        {patientData && (
          <div className="card mb-4">
            <div className="card-body bg-light-primary rounded-4">
              <div className="row g-3">
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Client Name:</span>
                  <span className="fw-bold text-dark">{patientData.first_name} {patientData.last_name}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">MR Number:</span>
                  <span className="fw-bold text-dark">{patientData.mr_no || "N/A"}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Date of Birth:</span>
                  <span className="fw-bold text-dark">
                    {patientData.dob ? new Date(patientData.dob).toLocaleDateString("en-GB") : "N/A"}
                  </span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Gender:</span>
                  <span className="fw-bold text-dark capitalize">{patientData.gender || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header border-bottom">
            <h5 className="mb-0">Edit Individual Rehabilitation Plan (2)</h5>
            <small className="text-muted">Client-Centred SMART Rehabilitation Plan (CARF-Aligned)</small>
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger m-4">{error}</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <IndividualRehabPlanTwoForm formData={formData} setFormData={setFormData} />
                <div className="row px-4 pb-4">
                  <div className="col-md-2 ms-auto">
                    <button type="submit" className="btn btn-primary w-100">
                      <i className="ri-save-3-line me-1"></i> Update
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditIndividualRehabPlanTwo;
